import { useFetchContentTypes } from '@utils/fetchContentTypes';
import { AsyncStorage } from '@utils/storage';

const ALLOWED_CONTENT_TYPES_KEY = 'allowedContentTypes';

/**
 * TA-10885: the verdict for one file, given whatever allow-list we could get.
 *
 * `null` (no list at all) has to mean "allow". The list is only ever populated
 * by a network fetch, so offline — or on a first run before any file has been
 * picked online — there is nothing to check against. Blocking there stops the
 * user attaching anything at all, and the server validates the content type on
 * upload regardless, so it is the safe side to fail to.
 */
export const isContentTypeAllowed = (
  storedContentTypes: string | null | undefined,
  contentType: string
): boolean =>
  storedContentTypes ? storedContentTypes.includes(contentType) : true;

export const useFileValidation = () => {
  const { fetchAllowedContentTypes } = useFetchContentTypes();

  const isValidExtension = async (contentType: string) => {
    const cached = await AsyncStorage.getItem(ALLOWED_CONTENT_TYPES_KEY);
    if (cached) {
      return isContentTypeAllowed(cached, contentType);
    }

    // TA-10885: fetch at most once, then decide on whatever we have.
    //
    // This used to recurse into itself after the fetch. fetchAllowedContentTypes
    // swallows its own errors and writes nothing when the request fails, so
    // offline the storage read came back empty again and the recursion never
    // terminated: the promise neither resolved nor rejected, and every cycle
    // re-hit /api/v3/allowed_content_types. validateFiles awaits this, so the
    // whole picker hung — the chosen photo never reached the composer and no
    // error was shown, which is why "offline upload" looked broken before the
    // upload had even been attempted.
    await fetchAllowedContentTypes();
    const refreshed = await AsyncStorage.getItem(ALLOWED_CONTENT_TYPES_KEY);

    return isContentTypeAllowed(refreshed, contentType);
  };

  return { isValidExtension };
};
